import { FC, useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import CN from 'classnames'
import {
  useGetPartnerOrderDetailsByWarehouseOrderNo,
  useUpdatePartnerOrder,
} from 'framework/api/methods'

import { Button, JsonLoader } from 'components/atoms'
import { Input } from 'components/atoms/Input'
import { TextArea } from 'components/atoms/TextArea'

export interface EditShippingAddressModalProps {
  orderDetails?: any
  onOverlayClick?: () => void | undefined
  setOpenEditShippingAddress: any
}

/** This is ShippingDetailViewSection */

export const EditShippingAddressModal: FC<EditShippingAddressModalProps> = ({
  orderDetails,
  onOverlayClick,
  setOpenEditShippingAddress,
}: EditShippingAddressModalProps) => {
  const [editViewShippingDetails, setEditViewShippingDetails] = useState<any>(
    {},
  )
  /** Handling Click Status */
  const handleOverlayClick = (event: any) => {
    if (onOverlayClick) {
      event.stopPropagation()
      onOverlayClick()
    }
  }

  const handleOnCloseClick = (event: any) => {
    event.stopPropagation()
    setOpenEditShippingAddress(false)
  }

  const disableOverlayClick = (event: any) => {
    event.stopPropagation()
  }
  const PopUpAlertClasses = CN(
    'PopUpAlertClasses fixed bg-black bg-opacity-50 w-full h-full z-40 top-0 right-0 bottom-0 left-0 flex items-center justify-center',
  )

  //  Toaster Function
  const notify = (message: any, type: any) => {
    if (type === 'error') {
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
    if (type === 'success') {
      toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
  }

  /** Get Partner Order Details By Id Success Function */
  const getPartnerOrderDetailsByIdOnSuccess = (data: any) => {
    const { partnerOrder } = data || {}
    //setOrderDetails(partnerOrder)
    //setIsDelivered(partnerOrder?.isDelivered)
    setEditViewShippingDetails({
      ...editViewShippingDetails,
      customerFirstName: partnerOrder?.customerFirstName || '',
      customerLastName: partnerOrder?.customerLastName || '',
      customerEmail: partnerOrder?.customerEmail || '',
      customerPhoneNo: partnerOrder?.customerPhone || '',
      customerAddressLine01: partnerOrder?.shippingAddress?.lineOne || '',
      customerAddressLine02: partnerOrder?.shippingAddress?.lineTwo || '',
      customerSuburb: partnerOrder?.shippingAddress?.suburb || '',
      customerState: partnerOrder?.shippingAddress?.state || '',
      customerCountry: partnerOrder?.shippingAddress?.country || '',
      customerCountryCode: partnerOrder?.shippingAddress?.countryCode || '',
      customerPostalCode: partnerOrder?.shippingAddress?.postCode || '',
      shippingNote: partnerOrder?.shippingNote || '',
      warehouseNote: partnerOrder?.warehouseNote || '',
      customerStateCode: partnerOrder?.shippingAddress?.stateCode || '',
      id: partnerOrder?.id,
    })
  }

  /** Get Partner Order Details By Id Error Function */
  const getPartnerOrderDetailsByIdOnError = (error: any) => {
    notify(error, 'error')
  }

  /** API Call for Get Partner Order Details By Id */
  const {
    isLoading: GetPartnerOrderDetailsByIdIsLoading,
    refetch: callGetPartnerOrderDetailsById,
  } = useGetPartnerOrderDetailsByWarehouseOrderNo(
    {
      wmsOrderNo: orderDetails.wmsOrderNo,
    },
    getPartnerOrderDetailsByIdOnSuccess,
    getPartnerOrderDetailsByIdOnError,
  )

  /** API Call for Partner Order Shipping info Update */
  const {
    mutate: updatePartnerOrderMutate,
    isLoading: updatePartnerOrderIsLoading,
  } = useUpdatePartnerOrder()

  /** Process the Partner Order Shipping info Update API call */
  async function updatePartnerOrder() {
    const updateVariables = {
      ...editViewShippingDetails,
      id: editViewShippingDetails.id,
    }
    updatePartnerOrderMutate(updateVariables, {
      onSuccess: ({ data: successData }: any) => {
        notify(successData?.message || '', 'success')
        setOpenEditShippingAddress(false)
      },

      onError: ({ response: { data: errData } }: any) => {
        notify(errData.message || 'Error...', 'error')
      },
    })
  }

  function handleSaveOnClick(e: any): void | undefined {
    e.preventDefault()
    updatePartnerOrder()
  }

  useEffect(() => {
    callGetPartnerOrderDetailsById()
  }, [])

  return (
    <div
      className={PopUpAlertClasses}
      role='button'
      tabIndex={0}
      onClick={(e: any) => handleOverlayClick(e)}
      onKeyDown={(e: any) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleOverlayClick(e)
        }
      }}>
      <div
        className='flex w-fit bg-white rounded-md flex-col items-center'
        role='button'
        tabIndex={0}
        onClick={(e: any) => disableOverlayClick(e)}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter' || e.key === ' ') {
            disableOverlayClick(e)
          }
        }}>
        <div className='flex w-full flex-col px-4 pt-[10px] mb-3'>
          <div className='flex items-center justify-between w-full px-4 py-3 border-b border-b-N-200'>
            <h2 className='text-lg font-bold'>
              {orderDetails.wmsOrderNo} - Shipping Address - Edit
            </h2>
            <button
              onClick={(e: any) => {
                handleOnCloseClick(e)
              }}
              className='bg-N-200 h-6 w-6 rounded-md hover:bg-N-300 accent-N-400'>
              <i className='ri-close-line'></i>
            </button>
          </div>
          <div className='flex w-full pt-6 px-4 max-w-[780px]'>
            <Input
              className='pr-2'
              labelValue='Customer First Name:'
              type='text'
              value={editViewShippingDetails.customerFirstName ?? ''}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerFirstName: e.target.value,
                })
              }}
            />

            <Input
              className='pl-2'
              labelValue='Customer Last Name:'
              type='text'
              value={editViewShippingDetails.customerLastName}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerLastName: e.target.value,
                })
              }}
            />
          </div>

          <div className='flex w-full pt-6 px-4 max-w-[780px]'>
            <Input
              className='pr-2'
              labelValue='Customer Email:'
              type='text'
              value={editViewShippingDetails.customerEmail}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerEmail: e.target.value,
                })
              }}
            />

            <Input
              className='pl-2'
              labelValue='Customer Phone:'
              type='text'
              value={editViewShippingDetails.customerPhoneNo}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerPhoneNo: e.target.value,
                })
              }}
            />
          </div>

          <div className='flex w-full pt-6 px-4 max-w-[780px]'>
            <Input
              labelValue='Address Line 01:'
              type='text'
              value={editViewShippingDetails.customerAddressLine01}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerAddressLine01: e.target.value,
                })
              }}
            />
          </div>

          <div className='flex w-full pt-6 px-4 max-w-[780px]'>
            <Input
              labelValue='Address Line 02:'
              type='text'
              value={editViewShippingDetails.customerAddressLine02}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerAddressLine02: e.target.value,
                })
              }}
            />
          </div>

          <div className='flex w-full pt-6 px-4 max-w-[780px]'>
            <Input
              className='pr-2'
              labelValue='Suburb:'
              type='text'
              value={editViewShippingDetails.customerSuburb}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerSuburb: e.target.value,
                })
              }}
            />
            <Input
              className='pl-2'
              labelValue='Postal Code:'
              type='text'
              value={editViewShippingDetails.customerPostalCode}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerPostalCode: e.target.value,
                })
              }}
            />
          </div>

          <div className='flex w-full pt-6 px-4 max-w-[780px]'>
            <Input
              className='pr-2'
              labelValue='State:'
              type='text'
              value={editViewShippingDetails.customerState}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerState: e.target.value,
                })
              }}
            />

            <Input
              labelValue='State Code:'
              className='pl-2'
              type='text'
              value={editViewShippingDetails.customerStateCode}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerStateCode: e.target.value,
                })
              }}
            />
          </div>
          <div className='flex w-full pt-6 px-4 min-w-[780px] max-w-[780px]'>
            <Input
              className='pr-2 w-1/2'
              labelValue='Country:'
              type='text'
              value={editViewShippingDetails.customerCountry}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerCountry: e.target.value,
                })
              }}
            />
            <Input
              className='pl-2 w-1/2'
              labelValue='Country Code:'
              type='text'
              value={editViewShippingDetails.customerCountryCode}
              onChange={(e: any) => {
                setEditViewShippingDetails({
                  ...editViewShippingDetails,
                  customerCountryCode: e.target.value,
                })
              }}
            />
          </div>
          <div className='w-full border-t border-t-N-200 mt-[10px] px-5 py-3 flex justify-end'>
            <Button
              className='w-[152px]'
              onClick={(e) => handleSaveOnClick(e)}
              disabled={false}
              isLoading={false}>
              Save
            </Button>
            <Button
              className='ml-5 w-[152px]'
              appearance='dull'
              disabled={false}
              onClick={handleOnCloseClick}>
              Close
            </Button>
          </div>
          {updatePartnerOrderIsLoading && (
            <div className='flex w-full h-full items-center justify-center bg-N-800 absolute z-[2] left-0 top-0 bg-opacity-20 cursor-not-allowed'>
              <JsonLoader size='xs' />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

EditShippingAddressModal.defaultProps = {
  onOverlayClick: undefined,
}

export default EditShippingAddressModal
